import { lazy, Suspense, useEffect, useState } from 'react';
import { BrandMark } from './components/BrandMark';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ApiError, api } from './api';
import type { WorkspaceResponse } from './api';
import { useRefreshFailures, useResource } from './hooks';
import { revalidateMounted } from './queryCache';
import { AcceptInvitationPage } from './pages/AcceptInvitationPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { AuthPage } from './pages/AuthPage';
import { EnvironmentPage } from './pages/EnvironmentPage';
import { LandingPage } from './pages/LandingPage';
import { MockCheckoutPage } from './pages/MockCheckoutPage';
import { CreateProjectPage } from './pages/CreateProjectPage';
import { CreateWordPressProjectPage } from './pages/CreateWordPressProjectPage';
import { OverviewPage } from './pages/OverviewPage';
import { ProjectIndexRedirect, ProjectShell } from './pages/project/ProjectShell';
import { ProjectsPage } from './pages/ProjectsPage';
import { ResetPasswordPage, RequestPasswordResetPage } from './pages/ResetPasswordPage';
import { ServersPage } from './pages/ServersPage';
import { ServerOnboardingPage } from './pages/ServerOnboardingPage';
import { ServerDetailPage } from './pages/ServerDetailPage';
import { SettingsPage, SettingsRedirect } from './pages/SettingsPage';
import { TeamPage } from './pages/TeamPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { StaffApp } from './pages/staff/StaffApp';
import { RouteErrorBoundary } from './components/SectionErrorBoundary';
import { Callout, NotFound } from './components/primitives';
import { AuthCardSkeleton, Skeleton, SkeletonPage, ThemeToggle } from './components/ui';
import { AppShell } from './components/layout/AppShell';
import { DeploymentToasts } from './components/DeploymentToasts';
import { TopProgressBar } from './components/TopProgressBar';
import { useLiveRefresh } from './liveRefresh';
import { usePrefetchOnIntent } from './prefetch';
import type { ReactNode } from 'react';

// The primitives gallery exists only in development builds (CO-88); the
// import is dropped from production bundles with the dead branch.
const UiGallery = import.meta.env.DEV ? lazy(() => import('./pages/UiGallery').then((m) => ({ default: m.UiGallery }))) : null;

/**
 * WorkspaceBanner states plainly which identity mode the API is running in.
 * The local development workspace is never presented as a signed-in session.
 */
function WorkspaceBanner({ workspace }: { workspace: WorkspaceResponse }) {
  return (
    <div className="banner banner--dev" data-testid="workspace-banner">
      <strong>Local development workspace — not authenticated.</strong> {workspace.identity.notice} Data belongs to{' '}
      <em>{workspace.organization.name}</em>.
    </div>
  );
}

/**
 * SafeModeBanner: after a recovery Commerce Ops starts no operation until
 * Hestaworks reconciles what was in flight (RES-006). Members see why their
 * deployments are paused instead of a 503 on every action. The text says
 * what safe mode is documented to do (internal/service/safe_mode.go,
 * docs/DISASTER_RECOVERY.md): no work is resumed, claimed or started, runs
 * in progress wait, and staff leave it after every server was checked. It
 * says nothing about the stores themselves, which Commerce Ops cannot see
 * from here (CO-184, OR20).
 */
function SafeModeBanner({ since }: { since: string | null | undefined }) {
  const when = since ? new Date(since).toLocaleString() : null;
  return (
    <Callout tone="warning" title="Commerce Ops is recovering" testId="safe-mode-banner" className="app__banner" role="status">
      Since {when ?? 'a recovery'}: deployments, restores and backups do not start, and operations that were running wait for a
      decision. Hestaworks checks every server before it leaves safe mode.
    </Callout>
  );
}

/**
 * AlertsReachNobodyBanner (CO-196): open alerts exist and no webhook or email
 * would carry them anywhere, so nobody has been told. Shown to owners only,
 * and only while there is something that went unheard.
 */
function AlertsReachNobodyBanner({ open }: { open: number }) {
  return (
    <Callout
      tone="danger"
      title={`${open} open ${open === 1 ? 'alert' : 'alerts'} reached nobody`}
      testId="alerts-reach-nobody-banner"
      className="app__banner"
      role="status"
      actions={
        <Link className="button button--secondary button--small" to="/app/settings?tab=alerts">
          Set up alerts
        </Link>
      }
    >
      No webhook or email is set up.
    </Callout>
  );
}

/**
 * StaleDataBanner: a background refresh failed, so what is on screen is the
 * last data Commerce Ops loaded. The page is not blanked (that would be worse
 * than useful), but it is never left pretending to be current either.
 */
function StaleDataBanner() {
  return (
    <Callout
      tone="warning"
      title="Some information could not be refreshed"
      testId="stale-data-banner"
      className="app__banner"
      role="status"
      actions={
        <button type="button" className="button button--secondary button--small" onClick={() => revalidateMounted()}>
          Try again
        </button>
      }
    >
      You are seeing what Commerce Ops last loaded, and it may be out of date.
    </Callout>
  );
}

/**
 * The one banner slot above every page (CO-184, OR20). At most one banner
 * shows, the most important first, so three notices never stack above the
 * page they are about:
 *
 *  1. safe mode: nothing starts, which changes what every action does;
 *  2. a refresh that failed: what is shown may be out of date;
 *  3. the local development workspace: a standing fact about the session.
 *
 * Safe mode is read again every minute, so the banner appears and goes
 * without a reload.
 */
export function AppBanner({ workspace }: { workspace: WorkspaceResponse }) {
  const platform = useResource(() => api.getPlatform(), [], undefined, { key: 'platform' });
  const reloadPlatform = platform.reload;
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') reloadPlatform();
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [reloadPlatform]);
  const failures = useRefreshFailures();
  // Only an owner can read the alert channels: others are never asked.
  const isOwner = Boolean(workspace.identity.role?.is_owner_role);
  const alerts = useResource(() => (isOwner ? api.getAlertChannels() : Promise.resolve(null)), [isOwner], undefined, { key: 'alert-channels' });
  const reloadAlerts = alerts.reload;
  useEffect(() => {
    if (!isOwner) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') reloadAlerts();
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [isOwner, reloadAlerts]);
  if (platform.data?.safe_mode) return <SafeModeBanner since={platform.data.since} />;
  if (failures > 0) return <StaleDataBanner />;
  if (alerts.data && !alerts.data.reachable && alerts.data.undelivered_open > 0) return <AlertsReachNobodyBanner open={alerts.data.undelivered_open} />;
  if (workspace.identity.kind !== 'session') return <WorkspaceBanner workspace={workspace} />;
  return null;
}

function Dashboard({ workspace, onSignOut }: { workspace: WorkspaceResponse; onSignOut: () => void }) {
  // Live events refresh what they touched, in place (one shared connection).
  useLiveRefresh();
  // Hovering or focusing a link warms the page it leads to.
  usePrefetchOnIntent();
  return (
    <AppShell workspace={workspace} onSignOut={onSignOut}>
        <main className="app__main" id="main-content" tabIndex={-1}>
          <AppBanner workspace={workspace} />
          <RouteErrorBoundary>
          <Routes>
            <Route path="/" element={<OverviewPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/new" element={<CreateProjectPage />} />
            <Route path="/projects/new/wordpress" element={<CreateWordPressProjectPage />} />
            {/* The project area is a shell with its own sections; the bare
                project URL still resolves, to the Overview. */}
            <Route path="/projects/:projectId" element={<ProjectIndexRedirect />} />
            <Route path="/projects/:projectId/*" element={<ProjectShell />} />
            <Route path="/environments/:environmentId" element={<EnvironmentPage />} />
            <Route path="/servers" element={<ServersPage />} />
            <Route path="/servers/new" element={<ServerOnboardingPage isOwner={Boolean(workspace.identity.role?.is_owner_role || workspace.identity.kind === 'dev_workspace')} />} />
            <Route path="/servers/:serverId" element={<ServerDetailPage />} />
            <Route path="/team" element={<TeamPage workspace={workspace} />} />
            <Route path="/settings" element={<SettingsPage workspace={workspace} />} />
            <Route path="/settings/integrations" element={<Navigate to="/app/settings?tab=integrations" replace />} />
            <Route path="/integrations" element={<Navigate to="/app/settings?tab=integrations" replace />} />
            <Route path="/billing" element={<SettingsRedirect tab="billing" />} />
            <Route path="/account" element={<SettingsRedirect tab="account" />} />
            <Route path="/audit-log" element={<AuditLogPage />} />
            <Route path="/capabilities" element={<SettingsRedirect tab="capabilities" />} />
            {UiGallery ? (
              <Route
                path="/__ui"
                element={
                  <Suspense fallback={<SkeletonPage rows={4} />}>
                    <UiGallery />
                  </Suspense>
                }
              />
            ) : null}
            <Route path="*" element={<NotFound home="/app" homeLabel="Go to the dashboard" />} />
          </Routes>
          </RouteErrorBoundary>
        </main>
      {/* Every page: one toast per running deployment (observation 3). */}
      <DeploymentToasts />
    </AppShell>
  );
}


/** AppGate is the entry point for every /app/* route: it resolves the
 * session, redirecting to /login when there is none, and only then renders
 * Dashboard. Kept separate from Dashboard so the public routes below never
 * pay for a workspace fetch they don't need. */
function AppGate() {
  const [authToken, setAuthToken] = useState(0);
  const workspace = useResource(() => api.workspace(), [authToken], undefined, { key: 'workspace' });
  const location = useLocation();

  if (workspace.loading) {
    // Shaped like Dashboard's own app-shell (sidebar + main content), not
    // the unrelated centered auth-shell used for sign-in — this is what
    // renders the instant a real session resolves, so the page should not
    // visibly restructure itself the moment that happens.
    return (
      <div className="app-shell" data-nav="loading" role="status" aria-label="Loading Commerce Ops">
        <aside className="sidebar sidebar--loading" aria-hidden="true">
          <div className="sidebar__inner">
            <div className="sidebar__brand-row">
              <div className="sidebar__brand">
                <BrandMark />
                <span>Commerce Ops</span>
              </div>
            </div>
            <div className="sidebar__skeleton">
              <Skeleton width="120px" height="12px" />
              <Skeleton width="100%" height="28px" />
              <Skeleton width="100%" height="28px" />
              <Skeleton width="100%" height="28px" />
              <Skeleton width="100%" height="28px" />
            </div>
          </div>
        </aside>
        <div className="app">
          <main className="app__main">
            <SkeletonPage cards={3} rows={3} />
          </main>
        </div>
      </div>
    );
  }

  const needsSignIn =
    workspace.error instanceof ApiError &&
    (workspace.error.code === 'not_authenticated' || workspace.error.code === 'session_invalid');
  if (needsSignIn) {
    // Carries the destination a signed-out visit (an invitation link, a
    // deployment link, a bookmark) was actually headed to through to
    // AuthPage, so a successful sign-in lands there instead of always at
    // /app — /app is the default destination anyway, so skip the param
    // for that one case.
    const destination = `${location.pathname}${location.search}`;
    const next = destination === '/app' ? '' : `?next=${encodeURIComponent(destination)}`;
    return <Navigate to={`/login${next}`} replace />;
  }

  if (workspace.error || !workspace.data) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-card__brand">
            <BrandMark size={26} />
            Commerce Ops
          </div>
          <Callout tone="danger" title="Commerce Ops is not reachable">
            {workspaceErrorMessage(workspace.error)}
          </Callout>
          <button className="button" type="button" onClick={() => setAuthToken((value) => value + 1)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (workspace.data.organization.suspended_at && workspace.data.identity.kind === 'session') {
    return <SuspendedOrganization workspace={workspace.data} onSignedOut={() => setAuthToken((value) => value + 1)} />;
  }

  return <Dashboard workspace={workspace.data} onSignOut={() => setAuthToken((value) => value + 1)} />;
}

/** The sentence under "Commerce Ops is not reachable" (CO-87): the API
 * client's own words when it has them, never a raw exception. */
export function workspaceErrorMessage(error: Error | null | undefined): string {
  if (error instanceof ApiError && error.message) return error.message;
  if (error) console.error('Loading the workspace failed:', error);
  return 'Your workspace could not be loaded. Try again in a moment.';
}

/** SuspendedOrganization replaces the dashboard while Hestaworks has
 * suspended the organization. The API refuses every other request with
 * `organization_suspended`, so rather than a page of errors this says once
 * what happened, what still runs, and what to do. The staff reason is never
 * sent to members. */
function SuspendedOrganization({ workspace, onSignedOut }: { workspace: WorkspaceResponse; onSignedOut: () => void }) {
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function signOut() {
    setSigningOut(true);
    setError(null);
    try {
      await api.logout();
      onSignedOut();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
      setSigningOut(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-shell__theme">
        <ThemeToggle />
      </div>
      <div className="auth-card suspended-card">
        <div className="auth-card__brand">
            <BrandMark size={26} />
            Commerce Ops
          </div>
        <h1>{workspace.organization.name} is suspended</h1>
        <p className="muted">
          Your servers, backups, monitoring and product feeds keep running. Nothing has been deleted.
        </p>
        <p className="muted">Contact Hestaworks to restore access.</p>
        {error ? (
          <Callout tone="danger">{error.message}</Callout>
        ) : null}
        <button className="button button--secondary" type="button" onClick={signOut} disabled={signingOut}>
          {signingOut ? 'Signing out' : 'Sign out'}
        </button>
      </div>
    </div>
  );
}

/** RequireGuest guards a public-only route (currently just /login):
 * someone with an already-valid real session who navigates there directly
 * — a stale bookmark, a back-button — is sent straight into the app
 * instead of being shown the sign-in form again. The local development
 * workspace's implicit, non-authenticated access does not trigger this:
 * it is not a session, and redirecting away from /login would make the
 * sign-in form itself unreachable to test while it's enabled. */
function RequireGuest({ children }: { children: ReactNode }) {
  const workspace = useResource(() => api.workspace(), []);

  if (workspace.loading) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <AuthCardSkeleton />
        </div>
      </div>
    );
  }
  if (workspace.data?.identity.kind === 'session') {
    return <Navigate to="/app" replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <>
      <TopProgressBar />
      <AppRoutes />
    </>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/login"
        element={
          <RequireGuest>
            <AuthPage />
          </RequireGuest>
        }
      />
      <Route path="/forgot-password" element={<RequestPasswordResetPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/set-password" element={<ResetPasswordPage isInitialSetup />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      <Route path="/mock-checkout" element={<MockCheckoutPage />} />
      <Route path="/app/*" element={<AppGate />} />
      {/* The Hestaworks staff console: its own sign-in and session, never a
          member's (docs/adr/0016-staff-console-and-addons.md). */}
      <Route path="/staff/*" element={<StaffApp />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
